/**
 * Italian Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const it: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Cruscotto',
  'nav.funds': 'Fondi',
  'nav.investments': 'Investimenti',
  'nav.investees': 'Partecipate',
  'nav.esg': 'ESG Revisione',
  'nav.compliance': 'Conformità',
  'nav.reports': 'Report',
  'nav.settings': 'Impostazioni',
  'nav.users': 'Utenti',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indicatori',
  'nav.taxonomy': 'Tassonomia UE',
  'nav.filings': 'Segnalazioni',
  'nav.calendar': 'Calendario',
  'nav.submit': 'Invia dati',
  'nav.submissions': 'Le mie sottomissioni',
  'nav.benchmarks': 'Benchmark',
  'nav.messages': 'Messaggi',
  'nav.portfolio': 'Portafoglio',
  'nav.performance': 'Performance dei fondi',
  'nav.esgSummary': 'ESG Riepilogo',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Metriche di impatto',
  'nav.sdg': 'Mappatura OSS',
  'nav.audit': 'Traccia di audit',
  'nav.snapshots': 'Snapshot dei dati',
  'nav.knowledgeGraph': 'Grafo della conoscenza',
  'nav.geographic': 'Geografico',
  'nav.bi': 'BI Studio',
  'nav.ai': 'Assistente AI',
  'nav.tasks': 'Attività',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Monitor normativo',

  // Common Actions
  'action.create': 'Crea',
  'action.edit': 'Modifica',
  'action.delete': 'Elimina',
  'action.save': 'Salva',
  'action.cancel': 'Annulla',
  'action.submit': 'Invia',
  'action.approve': 'Approva',
  'action.reject': 'Rifiuta',
  'action.download': 'Scarica',
  'action.export': 'Esporta',
  'action.import': 'Importa',
  'action.filter': 'Filtra',
  'action.search': 'Cerca',
  'action.refresh': 'Aggiorna',
  'action.close': 'Chiudi',
  'action.back': 'Indietro',
  'action.next': 'Avanti',
  'action.previous': 'Precedente',
  'action.viewAll': 'Visualizza tutto',
  'action.viewDetails': 'Visualizza dettagli',
  'action.generate': 'Genera',
  'action.signOut': 'Esci',
  'action.addNew': 'Aggiungi nuovo',
  'action.invite': 'Invita',

  // Statuses
  'status.draft': 'Bozza',
  'status.submitted': 'Inviato',
  'status.validated': 'Validato',
  'status.approved': 'Approvato',
  'status.rejected': 'Rifiutato',
  'status.pending': 'In attesa',
  'status.inProgress': 'In corso',
  'status.overdue': 'In ritardo',
  'status.compliant': 'Conforme',
  'status.filed': 'Depositato',
  'status.active': 'Attivo',
  'status.inactive': 'Inattivo',
  'status.closed': 'Chiuso',
  'status.fundraising': 'Raccolta fondi',
  'status.investing': 'Investimento',
  'status.harvesting': 'Raccolta',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Panoramica del portafoglio',
  'dashboard.complianceStatus': 'Stato di conformità',
  'dashboard.collectionProgress': 'Progresso della raccolta',
  'dashboard.activityFeed': 'Attività',
  'dashboard.quickActions': 'Azioni rapide',
  'dashboard.investmentPipeline': 'Pipeline di investimento',
  'dashboard.totalFunds': 'Totale fondi',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Investimenti attivi',
  'dashboard.totalInvestees': 'Partecipate',
  'dashboard.recentActivity': 'Attività recente',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Totale impegnato',
  'lp.capitalCalled': 'Capitale richiamato',
  'lp.distributed': 'Distribuito',
  'lp.netPosition': 'Posizione netta',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Performance dei fondi',
  'lp.esgSummary': 'ESG Riepilogo',
  'lp.reportLibrary': 'Libreria report',
  'lp.taxonomyAligned': 'Allineato alla tassonomia',
  'lp.paiCoverage': 'PAI Copertura',
  'lp.portfolioPerformance': 'Performance portafoglio LP',

  // Fund Labels
  'fund.name': 'Nome del fondo',
  'fund.code': 'Codice del fondo',
  'fund.vintage': 'Anno di costituzione',
  'fund.size': 'Dimensione del fondo',
  'fund.targetSize': 'Dimensione target',
  'fund.currency': 'Valuta',
  'fund.strategy': 'Strategia',
  'fund.status': 'Stato',
  'fund.sfdrCategory': 'SFDR Categoria',
  'fund.geographyFocus': 'Focus geografico',
  'fund.sectorFocus': 'Focus settoriale',

  // Form Labels
  'form.name': 'Nome',
  'form.email': 'E-mail',
  'form.country': 'Paese',
  'form.amount': 'Importo',
  'form.currency': 'Valuta',
  'form.date': 'Data',
  'form.description': 'Descrizione',
  'form.notes': 'Note',
  'form.type': 'Tipo',
  'form.period': 'Periodo',
  'form.year': 'Anno',
  'form.required': 'Obbligatorio',
  'form.optional': 'Facoltativo',

  // Table Headers
  'table.fund': 'Fondo',
  'table.commitment': 'Impegno',
  'table.called': 'Richiamato',
  'table.distributed': 'Distribuito',
  'table.tvpi': 'TVPI',
  'table.status': 'Stato',
  'table.regulation': 'Regolamento',
  'table.filingType': 'Tipo di segnalazione',
  'table.reportingPeriod': 'Periodo',
  'table.createdAt': 'Creato',
  'table.actions': 'Azioni',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Tassonomia UE',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Completezza dei dati',
  'compliance.nextDeadline': 'Prossima scadenza',

  // Reports
  'report.quarterly': 'Report trimestrale',
  'report.annual': 'Report annuale',
  'report.paiStatement': 'PAI Dichiarazione',
  'report.taxonomyReport': 'Report tassonomia',
  'report.generatedAt': 'Generato',
  'report.version': 'Versione',

  // General
  'general.loading': 'Caricamento...',
  'general.noData': 'Nessun dato disponibile',
  'general.error': 'Si è verificato un errore',
  'general.retry': 'Riprova',
  'general.total': 'Totale',
  'general.page': 'Pagina',
  'general.of': 'di',
  'general.showing': 'Visualizzazione',
  'general.results': 'risultati',
  'general.language': 'Lingua',
  'general.english': 'Inglese',
  'general.french': 'Francese',

  // Tour
  'tour.takeTour': 'Fai il tour',
  'tour.skip': 'Salta',
  'tour.next': 'Avanti',
  'tour.prev': 'Indietro',
  'tour.finish': 'Fine',
};
